import type { z } from 'zod';
import { toVerifiedType } from '@/lib/twitter/verification';
import { ProfileResponseSchema } from '@/lib/validation/api/pulse/schemas';
import { normalizeProfileResponsePayload, type ProfileResponsePayload } from '@/lib/validation/api/pulse/serializers';
import type { TweetAuthorAffiliation } from '@/types/tweet';
import type { UserEntities } from '@/types/user-profile';

type VerifiedType = 'none' | 'blue' | 'business' | 'government';

// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion, typescript-eslint/no-unsafe-call
const normalizeVerifiedType = (input: unknown): VerifiedType => toVerifiedType(input) as VerifiedType;

export type ProfileResponse = z.infer<typeof ProfileResponseSchema>;

export interface UserPreviewVM {
  affiliations?: TweetAuthorAffiliation[];
  createdAt?: Date | null;
  description?: string | null;
  entities?: UserEntities;
  followersCount?: number;
  followingCount?: number;
  followsYou?: boolean;
  id: string;
  isFollowing?: boolean;
  listedCount?: number;
  location?: string | null;
  name: string;
  primaryAffiliation?: TweetAuthorAffiliation | null;
  profileBannerUrl?: string | null;
  profileImageUrl?: string | null;
  totalInteractions?: number;
  tweetCount?: number;
  url?: string | null;
  username: string;
  verifiedType: VerifiedType;
}

const UserPreviewTransformer = ProfileResponseSchema.transform<UserPreviewVM>((payload) => {
  const preview: UserPreviewVM = {
    id: payload.id,
    username: payload.username,
    name: payload.name,
    verifiedType: normalizeVerifiedType(payload.verifiedType),
  };
  if (payload.description !== undefined) {
    preview.description = payload.description ?? null;
  }
  if (payload.profileImageUrl !== undefined) {
    preview.profileImageUrl = payload.profileImageUrl ?? null;
  }
  if (payload.profileBannerUrl !== undefined) {
    preview.profileBannerUrl = payload.profileBannerUrl ?? null;
  }
  if (typeof payload.totalInteractions === 'number') {
    preview.totalInteractions = payload.totalInteractions;
  }
  if (typeof payload.followersCount === 'number') {
    preview.followersCount = payload.followersCount;
  }
  if (typeof payload.followingCount === 'number') {
    preview.followingCount = payload.followingCount;
  }
  if (typeof payload.tweetCount === 'number') {
    preview.tweetCount = payload.tweetCount;
  }
  if (typeof payload.listedCount === 'number') {
    preview.listedCount = payload.listedCount;
  }
  if (Array.isArray(payload.affiliations)) {
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    preview.affiliations = payload.affiliations as TweetAuthorAffiliation[];
  }
  if (payload.primaryAffiliation !== undefined) {
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    preview.primaryAffiliation = (payload.primaryAffiliation as TweetAuthorAffiliation | null) ?? null;
  }
  if (payload.location !== undefined) {
    preview.location = payload.location ?? null;
  }
  if (payload.url !== undefined) {
    preview.url = payload.url ?? null;
  }
  if (payload.entities !== undefined) {
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    preview.entities = payload.entities as UserEntities;
  }
  if (payload.createdAt !== undefined) {
    preview.createdAt = payload.createdAt ?? null;
  }
  const isFollowing = (payload as { isFollowing?: boolean }).isFollowing;
  if (isFollowing !== undefined) {
    preview.isFollowing = isFollowing;
  }
  const followsYou = (payload as { followsYou?: boolean }).followsYou;
  if (followsYou !== undefined) {
    preview.followsYou = followsYou;
  }
  return preview;
});

export function fromProfileResponse(resp: ProfileResponsePayload): UserPreviewVM {
  // Accept both raw objects and serialized payloads so hover cards can reuse
  // prefetched cache entries regardless of how they crossed the wire.
  const normalized = normalizeProfileResponsePayload(resp);
  return UserPreviewTransformer.parse(normalized);
}
